<template>
  <div v-html="year.turn_in_criteria_text"></div>
  <v-divider></v-divider>
  <v-form ref="form" v-model="isFormValid" @submit.prevent="onSubmit">
    <v-card title="Mandatory Criteria">
      <v-card-text>
        <CriteriaTurnInComponent
          :selection="manditoryCriteria"
          :is-manditory="true"
          :is-required="true"
          @update="updateCrit"
          @remove="removeCrit"></CriteriaTurnInComponent>
      </v-card-text>
      <v-card-actions>
        <FullWidthButton :disabled="!isFormValid" @click="onSubmit">Save</FullWidthButton>
      </v-card-actions>
    </v-card>
    <v-card
      v-if="year.allow_optional_criteria == true"
      class="mt-4"
      title="Optional Criteria"
      subtitle="To add Optional Criteria please use the +">
      <template v-slot:append>
        <v-icon icon="mdi-plus" @click="openModal('addOptional')"></v-icon
        ><v-tooltip activator="parent" location="end">Add Optional Criteria</v-tooltip>
      </template>
      <v-card-text>
        <div v-if="optionalCriteria.length == 0" class="text-center">
          Click the <v-icon icon="mdi-plus"> </v-icon> if you would like to add additional criteria
          that you did.
        </div>
        <CriteriaTurnInComponent
          v-else
          :selection="optionalCriteria"
          :is-manditory="false"
          :is-required="true"
          @update="updateCrit"
          @remove="removeCrit"></CriteriaTurnInComponent>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="primary"
          block
          class="rounded-lg mb-8 mt-8"
          elevation="2"
          :disabled="!isFormValid"
          @click="onSubmit"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-form>
  <v-dialog v-model="showModal" max-width="800px">
    <v-sheet class="bg-background flex-1-1 pa-4" max-width="600">
      <CriteriaSelectionCompontent
        :year-id="year.id"
        :team-id="team.id"
        :adding-optional="true"
        :branch-year="registration.branchYear ?? new BranchYearDTO()"
        :used-criteria="usedCriterias"
        @close="showModal = false">
      </CriteriaSelectionCompontent>
    </v-sheet>
  </v-dialog>
</template>
<script setup lang="ts">
import {
  BranchYearDTO,
  ICriteriaDTO,
  ITeamCriteriaDTO,
  ITeamDTO,
  IYearDTO,
  TeamCriteriaSelectionDTO,
} from '@/dtos';
import { computed, ref, watch } from 'vue';
import { useSubmissionnStore } from '@/stores';
import type { VForm } from 'vuetify/components';
import CriteriaTurnInComponent from '@/components/criteria/CriteriaTurnInComponent.vue';
import CriteriaSelectionCompontent from '@/components/criteria/CriteriaSelectionCompontent.vue';

interface Props {
  registration: ITeamCriteriaDTO;
  year: IYearDTO;
  team: ITeamDTO;
}

const props = defineProps<Props>();
const year = computed(() => props.year);
const registration = computed(() => props.registration);
const submissionStore = useSubmissionnStore();
const manditoryCriteria = ref<TeamCriteriaSelectionDTO[]>([]);
const optionalCriteria = ref<TeamCriteriaSelectionDTO[]>([]);
const usedCriterias = ref<ICriteriaDTO[]>([]);
const teamCriteriaId = computed(() => registration.value.id);
const { data } = submissionStore.getCriteriaSelectionsQuery(teamCriteriaId);
const showModal = ref<boolean>(false);
const isFormValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>();

watch(
  () => data.value,
  () => {
    manditoryCriteria.value =
      data.value?.data.filter((critiera) => critiera.mandatory == true) ?? [];
    optionalCriteria.value =
      data.value?.data.filter((critiera) => critiera.mandatory == false) ?? [];
    const criterias = data.value?.data
      ?.map((crit) => crit.criteria)
      .filter((criteria) => criteria !== undefined);
    if (criterias && criterias.length > 0) {
      usedCriterias.value = criterias;
    }
  },
);
const openModal = async (type = {}) => {
  if (type === 'addOptional') {
    showModal.value = true;
  }
};

const removeCrit = async (item: TeamCriteriaSelectionDTO, isManditory: boolean) => {
  if (!isManditory) {
    optionalCriteria.value = optionalCriteria.value.filter((crit) => crit.id !== item.id);
    submissionStore.removeOptionalCriteria.mutateAsync(item.id);
  }
};

const updateCrit = async (item: TeamCriteriaSelectionDTO, isManditory: boolean) => {
  if (isManditory) {
    manditoryCriteria.value = manditoryCriteria.value.map((crit) => {
      if (crit.id === item.id) {
        return item;
      }
      return crit;
    });
  } else {
    optionalCriteria.value = optionalCriteria.value.map((crit) => {
      if (crit.id === item.id) {
        return item;
      }
      return crit;
    });
  }
};

const onSubmit = async () => {
  manditoryCriteria.value.forEach((crit) => {
    submissionStore.updateCriteriaSelection.mutateAsync(crit);
  });
  optionalCriteria.value.forEach((crit) => {
    submissionStore.updateCriteriaSelection.mutateAsync(crit);
  });
};
</script>
